import { useMemo, useState } from "react";
import { Card, CardContent } from "../../../components/ui-custom/Card";
import Button from "components/ui-custom/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui-custom/Dialog";
import { Input } from "../../../components/ui-custom/Input";
import { Label } from "../../../components/ui-custom/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui-custom/Select";
import {
  IndianRupee,
  Layers,
  ListChecks,
  TrendingUp,
  PlusCircle,
  Workflow,
  AlertTriangle,
  Loader2,
  Link2,
  Search,
  X,
} from "lucide-react";
import { formatCurrency, getAllTotals } from "./boqData";
import BoQSectionCard from "./BoQSectionCard";
import { toast } from "sonner";
import { BoQApi } from "../../../api/boq/service";
import { taskApi } from "../../../api/tasks/taskApi";

const ITEM_UNIT_OPTIONS = ["sqm", "cum", "sqft", "point", "set", "rmt", "nos", "kg", "point", "lot", "ls", "m3", "rft",];

const initialItemState = {
  itemName: "",
  unit: "nos",
  customUnit: "",
  quantity: "",
  labourCost: "",
  materialCost: "",
  profitPercent: 10,
};

const getItemFormState = (item = null) => {
  if (!item) return initialItemState;

  const rawUnit = String(item?.unit || item?.raw?.uom || "nos").trim();
  const normalizedUnit = rawUnit.toLowerCase();
  const usesCustomUnit =
    rawUnit && !ITEM_UNIT_OPTIONS.includes(normalizedUnit);

  return {
    itemName: item?.description || item?.raw?.item_name || "",
    unit: usesCustomUnit ? "custom" : normalizedUnit || "nos",
    customUnit: usesCustomUnit ? rawUnit : "",
    quantity: item?.quantity ?? "",
    labourCost: item?.labourCost ?? "",
    materialCost: item?.materialCost ?? "",
    profitPercent: item?.profitPercent ?? 10,
  };
};

const normalizeTaskOption = (task) => {
  const id = task?.id ?? task?.task_id;

  if (!id) return null;

  return {
    id,
    name: task?.task_name || task?.name || `Task #${id}`,
    groupId: task?.task_group_id ?? task?.task_group ?? null,
    groupName: task?.task_group_name || task?.task_group || "Ungrouped",
    status: task?.task_status || task?.status || "",
  };
};

const BoQItemForm = ({ itemState, setItemState, totalPrice, rate }) => {
  const handleFieldChange = (field, value) => {
    setItemState((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-1.5">
        <Label className="text-sm">Item Name</Label>
        <Input
          placeholder="e.g. Polishing"
          value={itemState.itemName}
          onChange={(e) => handleFieldChange("itemName", e.target.value)}
          className="h-9 text-sm px-3"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Quantity</Label>
          <Input
            type="number"
            value={itemState.quantity}
            onChange={(e) => handleFieldChange("quantity", e.target.value)}
            className="h-11 text-sm px-3"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Unit</Label>
          <Select
            value={itemState.unit}
            onValueChange={(value) =>
              setItemState((prev) => ({
                ...prev,
                unit: value,
                customUnit: value === "custom" ? prev.customUnit : "",
              }))
            }
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ITEM_UNIT_OPTIONS.map((unitOption) => (
                <SelectItem key={unitOption} value={unitOption}>
                  {unitOption}
                </SelectItem>
              ))}
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {itemState.unit === "custom" && (
        <div className="space-y-1.5">
          <Label className="text-sm">Custom Unit</Label>
          <Input
            placeholder="e.g. bag, ft, box"
            value={itemState.customUnit}
            onChange={(e) => handleFieldChange("customUnit", e.target.value)}
            className="h-9 text-sm px-3"
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Material Estimation (₹)</Label>
          <Input
            type="number"
            value={itemState.materialCost}
            onChange={(e) => handleFieldChange("materialCost", e.target.value)}
            className="h-9 text-sm px-3"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Labour Estimation (₹)</Label>
          <Input
            type="number"
            value={itemState.labourCost}
            onChange={(e) => handleFieldChange("labourCost", e.target.value)}
            className="h-9 text-sm px-3"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">Profit Margin %</Label>
        <Input
          type="number"
          value={itemState.profitPercent}
          onChange={(e) => handleFieldChange("profitPercent", e.target.value)}
          className="h-9 text-sm px-3"
        />
      </div>
      <div className="rounded-lg border border-border/60 bg-muted/20 p-3 text-sm space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total Price</span>
          <span className="font-semibold">{formatCurrency(totalPrice)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Rate</span>
          <span className="font-semibold">{formatCurrency(rate)}</span>
        </div>
      </div>
    </div>
  );
};

const BoQSetup = ({ sections, onRefresh, onGenerateTasks, generatingTasks, refreshing }) => {
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddItem, setShowAddItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [linkingSection, setLinkingSection] = useState(null);
  const [linkingItem, setLinkingItem] = useState(null);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [editSectionName, setEditSectionName] = useState("");
  const [newItem, setNewItem] = useState(initialItemState);
  const [editItem, setEditItem] = useState(initialItemState);
  const [sectionTaskGroups, setSectionTaskGroups] = useState([]);
  const [selectedTaskGroupId, setSelectedTaskGroupId] = useState("");
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [creatingSection, setCreatingSection] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);
  const [updatingSection, setUpdatingSection] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(false);
  const [loadingSectionTaskGroups, setLoadingSectionTaskGroups] = useState(false);
  const [savingSectionLink, setSavingSectionLink] = useState(false);
  const [loadingTaskOptions, setLoadingTaskOptions] = useState(false);
  const [savingItemLink, setSavingItemLink] = useState(false);
  const [deletingSectionId, setDeletingSectionId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const totals = getAllTotals(sections);

  const quantity = Number(newItem.quantity) || 0;
  const labourCost = Number(newItem.labourCost) || 0;
  const materialCost = Number(newItem.materialCost) || 0;
  const resolvedUnit = newItem.unit === "custom" ? newItem.customUnit.trim() : newItem.unit;
  const isUnitValid = Boolean(resolvedUnit);
  const totalPrice = labourCost + materialCost;
  const rate = quantity > 0 ? totalPrice / quantity : 0;

  const editQuantity = Number(editItem.quantity) || 0;
  const editLabourCost = Number(editItem.labourCost) || 0;
  const editMaterialCost = Number(editItem.materialCost) || 0;
  const editResolvedUnit =
    editItem.unit === "custom" ? editItem.customUnit.trim() : editItem.unit;
  const isEditUnitValid = Boolean(editResolvedUnit);
  const editTotalPrice = editLabourCost + editMaterialCost;
  const editRate = editQuantity > 0 ? editTotalPrice / editQuantity : 0;

  const filteredTaskOptions = useMemo(() => {
    const query = taskSearchTerm.trim().toLowerCase();

    if (!query) return taskOptions;

    return taskOptions.filter((task) => {
      const name = String(task?.name || "").toLowerCase();
      const groupName = String(task?.groupName || "").toLowerCase();
      const status = String(task?.status || "").toLowerCase();

      return (
        name.includes(query) ||
        groupName.includes(query) ||
        status.includes(query)
      );
    });
  }, [taskOptions, taskSearchTerm]);

  const groupedTaskOptions = useMemo(() => {
    const groups = new Map();

    filteredTaskOptions.forEach((task) => {
      const key = task.groupId ?? `group-${task.groupName}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          groupName: task.groupName || "Ungrouped",
          tasks: [],
        });
      }

      groups.get(key).tasks.push(task);
    });

    return Array.from(groups.values());
  }, [filteredTaskOptions]);

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;

    try {
      setCreatingSection(true);
      await BoQApi.createSection({
        sectionName: newSectionName.trim(),
        order: sections.length,
      });
      toast.success("Section added");
      setShowAddSection(false);
      setNewSectionName("");
      await onRefresh();
    } catch (error) {
      console.error("Failed to create BoQ section", error);
      toast.error(error.message || "Failed to create section");
    } finally {
      setCreatingSection(false);
    }
  };

  const handleAddItem = async () => {
    if (!showAddItem || !newItem.itemName.trim() || quantity <= 0 || !isUnitValid) return;

    try {
      setCreatingItem(true);
      await BoQApi.createItem({
        boq_section: showAddItem,
        item_name: newItem.itemName.trim(),
        quantity,
        uom: resolvedUnit,
        material_estimation: materialCost,
        labor_estimation: labourCost,
        profit_margin: Number(newItem.profitPercent) || 0,
      });
      toast.success("Item added");
      setShowAddItem(null);
      setNewItem(initialItemState);
      await onRefresh();
    } catch (error) {
      console.error("Failed to create BoQ item", error);
      toast.error(error.message || "Failed to create item");
    } finally {
      setCreatingItem(false);
    }
  };

  const closeAddItemModal = () => {
    setShowAddItem(null);
    setNewItem(initialItemState);
  };

  const openEditSectionModal = (section) => {
    setEditingSection(section);
    setEditSectionName(section?.name || "");
  };

  const closeEditSectionModal = () => {
    setEditingSection(null);
    setEditSectionName("");
  };

  const openEditItemModal = (section, item) => {
    setEditingItem({
      id: item.id,
      sectionId: section.id,
      code: item.code,
      name: item.description,
    });
    setEditItem(getItemFormState(item));
  };

  const closeEditItemModal = () => {
    setEditingItem(null);
    setEditItem(initialItemState);
  };

  const openLinkSectionModal = async (section) => {
    setLinkingSection(section);
    setSelectedTaskGroupId("");
    setSectionTaskGroups([]);

    try {
      setLoadingSectionTaskGroups(true);
      const groups = await taskApi.getAllTaskGroups();
      const filteredGroups = groups.filter((group) => group.is_group_linked === false);
      setSectionTaskGroups(Array.isArray(filteredGroups) ? filteredGroups : []);
    } catch (error) {
      console.error("Failed to load task groups", error);
      toast.error(error.message || "Failed to load task groups");
      setSectionTaskGroups([]);
    } finally {
      setLoadingSectionTaskGroups(false);
    }
  };

  const closeLinkSectionModal = () => {
    setLinkingSection(null);
    setSelectedTaskGroupId("");
    setSectionTaskGroups([]);
  };

  const openLinkItemModal = async (section, item) => {
    setLinkingItem({
      id: item.id,
      name: item.description,
      sectionName: section.name,
      sectionTaskGroup: section.taskGroup,
    });
    setTaskSearchTerm("");
    setSelectedTaskId("");
    setTaskOptions([]);

    try {
      setLoadingTaskOptions(true);
      const tasks = section.taskGroup != null
        ? await taskApi.getAllTasks(section.taskGroup)
        : await taskApi.getTasksBySite();
      const filteredTasks = tasks.filter((task) => task.is_linked === false);
      const normalizedTasks = (Array.isArray(filteredTasks) ? filteredTasks : [])
        .map(normalizeTaskOption)
        .filter(Boolean);
      setTaskOptions(normalizedTasks);
    } catch (error) {
      console.error("Failed to load tasks", error);
      toast.error(error.message || "Failed to load tasks");
      setTaskOptions([]);
    } finally {
      setLoadingTaskOptions(false);
    }
  };

  const closeLinkItemModal = () => {
    setLinkingItem(null);
    setTaskSearchTerm("");
    setTaskOptions([]);
    setSelectedTaskId("");
  };

  const handleUpdateSection = async () => {
    if (!editingSection?.id || !editSectionName.trim()) return;

    try {
      setUpdatingSection(true);
      await BoQApi.updateSection(editingSection.id, {
        section_name: editSectionName.trim(),
        order: sections.length + 1,
        site: editingSection?.site,
        created_by: editingSection?.createdBy
      });
      toast.success("Section updated");
      closeEditSectionModal();
      await onRefresh();
    } catch (error) {
      console.error("Failed to update BoQ section", error);
      toast.error(error.message || "Failed to update section");
    } finally {
      setUpdatingSection(false);
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem?.id || !editItem.itemName.trim() || editQuantity <= 0 || !isEditUnitValid) {
      return;
    }

    try {
      setUpdatingItem(true);
      await BoQApi.updateItem(editingItem.id, {
        item_name: editItem.itemName.trim(),
        quantity: editQuantity,
        uom: editResolvedUnit,
        material_estimation: editMaterialCost,
        labor_estimation: editLabourCost,
        profit_margin: Number(editItem.profitPercent) || 0,
      });
      toast.success("Item updated");
      closeEditItemModal();
      await onRefresh();
    } catch (error) {
      console.error("Failed to update BoQ item", error);
      toast.error(error.message || "Failed to update item");
    } finally {
      setUpdatingItem(false);
    }
  };

  const handleDeleteSection = async (section) => {
    if (!section?.id) return;

    const confirmed = window.confirm(`Delete section "${section.name}"?`);
    if (!confirmed) return;

    try {
      setDeletingSectionId(section.id);
      await BoQApi.deleteSection(section.id);
      toast.success("Section deleted");
      await onRefresh();
    } catch (error) {
      console.error("Failed to delete BoQ section", error);
      toast.error(error.message || "Failed to delete section");
    } finally {
      setDeletingSectionId(null);
    }
  };

  const handleLinkSection = async () => {
    if (!linkingSection?.id || !selectedTaskGroupId) return;

    try {
      setSavingSectionLink(true);
      await BoQApi.updateSection(linkingSection.id, {
        task_group: Number(selectedTaskGroupId),
        section_name: linkingSection.name,
        order: linkingSection.order,
        site: linkingSection.site,
        created_by: linkingSection.createdBy,
      });
      toast.success("Section linked to task group");
      closeLinkSectionModal();
      await onRefresh();
    } catch (error) {
      console.error("Failed to link section", error);
      toast.error(error.message || "Failed to link section");
    } finally {
      setSavingSectionLink(false);
    }
  };

  const handleDeleteItem = async (section, item) => {
    if (!item?.id) return;

    const confirmed = window.confirm(
      `Delete BoQ item "${item.description}" from "${section.name}"?`
    );
    if (!confirmed) return;

    try {
      setDeletingItemId(item.id);
      await BoQApi.deleteItem(item.id);
      toast.success("BoQ item deleted");
      await onRefresh();
    } catch (error) {
      console.error("Failed to delete BoQ item", error);
      toast.error(error.message || "Failed to delete BoQ item");
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleLinkItem = async () => {
    if (!linkingItem?.id || !selectedTaskId) return;

    try {
      setSavingItemLink(true);
      await BoQApi.linkItemToTask({
        task_id: Number(selectedTaskId),
        boq_item_id: linkingItem.id,
      });
      toast.success("BoQ item linked to task");
      closeLinkItemModal();
      await onRefresh();
    } catch (error) {
      console.error("Failed to link BoQ item", error);
      toast.error(error.message || "Failed to link BoQ item");
    } finally {
      setSavingItemLink(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-5 gap-4">
        {[
          {
            icon: IndianRupee,
            label: "TOTAL BUDGET",
            value: formatCurrency(totals.totalBudget),
            indicator: "Estimated",
            indicatorColor: "text-success",
            indicatorIcon: TrendingUp,
          },
          {
            icon: TrendingUp,
            label: "ACTUAL COST",
            value: formatCurrency(totals.totalActual),
            indicator: "live",
            indicatorColor: "text-success",
            indicatorIcon: TrendingUp,
          },
          {
            icon: Layers,
            label: "SECTIONS",
            value: sections.length.toString(),
            indicator: `${sections.length} groups`,
            indicatorColor: "text-secondary",
            indicatorIcon: Layers,
          },
          {
            icon: ListChecks,
            label: "TOTAL ITEMS",
            value: totals.totalItems.toString(),
            indicator: "Line items",
            indicatorColor: "text-info",
            indicatorIcon: ListChecks,
          },
          {
            icon: TrendingUp,
            label: "EST. PROFIT",
            value: `${totals.avgProfit.toFixed(1)}%`,
            indicator: "Avg margin",
            indicatorColor: "text-success",
            indicatorIcon: TrendingUp,
          }
        ].map((card) => (
          <Card key={card.label} className="border-border/60 shadow-none">
            <CardContent>
              <p className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase mb-2">{card.label}</p>
              <p className="text-xl font-bold text-foreground">{card.value}</p>
              <div className={`flex items-center gap-1 mt-1.5 ${card.indicatorColor}`}>
                <card.indicatorIcon className="h-3 w-3" />
                <span className="text-[11px] font-medium">{card.indicator}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          className="h-7 text-sm gap-1.5 bg-sm-primary hover:bg-sm-primary/90 text-white "
          onClick={() => setShowGenerateConfirm(true)}
          disabled={generatingTasks || refreshing || sections.length === 0}
        >
          {generatingTasks ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Workflow className="h-3.5 w-3.5" />}
          Generate Task Structure
        </Button>

        <Button
          variant="primary"
          size="sm"
          className="h-7 text-sm gap-1.5 border-border/60"
          onClick={() => setShowAddSection(true)}
          disabled={refreshing}
        >
          <PlusCircle className="h-3.5 w-3.5" /> Add Section
        </Button>
      </div>

      <div className="space-y-3">
        {sections.length === 0 ? (
          <Card className="border-border/60 shadow-none">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No BoQ sections found for this site yet.
            </CardContent>
          </Card>
        ) : (
          sections.map((section) => (
            <BoQSectionCard
              key={section.id}
              section={section}
              onAddItem={(sectionId) => setShowAddItem(sectionId)}
              onEditSection={openEditSectionModal}
              onDeleteSection={handleDeleteSection}
              onLinkSection={openLinkSectionModal}
              onEditItem={openEditItemModal}
              onDeleteItem={handleDeleteItem}
              onLinkItem={openLinkItemModal}
            />
          ))
        )}
      </div>

      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Add Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Section Name</Label>
              <Input
                placeholder="e.g. Flooring"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                className="h-9 text-sm px-2"
              />
            </div>
            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleAddSection}
              disabled={!newSectionName.trim() || creatingSection}
            >
              {creatingSection ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Section"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showAddItem} onOpenChange={(open) => !open && closeAddItemModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-base">Add BoQ Item</DialogTitle>
            {/* Close */}
            <Button
              variant="ghost"
              size="icon"
              onClick={closeAddItemModal}
            >
              <X className="h-5 w-5" />
            </Button>

          </DialogHeader>
          <div className="space-y-3 pt-2">
            <BoQItemForm
              itemState={newItem}
              setItemState={setNewItem}
              totalPrice={totalPrice}
              rate={rate}
            />
            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleAddItem}
              disabled={!newItem.itemName.trim() || quantity <= 0 || !isUnitValid || creatingItem}
            >
              {creatingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingSection} onOpenChange={(open) => !open && closeEditSectionModal()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-base">Edit Section</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeEditSectionModal}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Section Name</Label>
              <Input
                placeholder="e.g. Flooring"
                value={editSectionName}
                onChange={(e) => setEditSectionName(e.target.value)}
                className="h-9 text-sm px-2"
              />
            </div>
            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleUpdateSection}
              disabled={!editSectionName.trim() || updatingSection}
            >
              {updatingSection ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingItem} onOpenChange={(open) => !open && closeEditItemModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-base">Edit BoQ Item</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeEditItemModal}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <BoQItemForm
              itemState={editItem}
              setItemState={setEditItem}
              totalPrice={editTotalPrice}
              rate={editRate}
            />
            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleUpdateItem}
              disabled={!editItem.itemName.trim() || editQuantity <= 0 || !isEditUnitValid || updatingItem}
            >
              {updatingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkingSection} onOpenChange={(open) => !open && closeLinkSectionModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-base">Link Section</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeLinkSectionModal}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Section:</span>{" "}
              <span className="font-medium text-foreground">{linkingSection?.name}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Task Group</Label>
              <Select value={selectedTaskGroupId} onValueChange={setSelectedTaskGroupId}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select a task group" />
                </SelectTrigger>
                <SelectContent>
                  {sectionTaskGroups.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.group_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {loadingSectionTaskGroups ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading task groups...
              </div>
            ) : sectionTaskGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground text-center">
                No unlinked task groups available to link to this section.
              </div>
            ) : null}
            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleLinkSection}
              disabled={!selectedTaskGroupId || savingSectionLink || loadingSectionTaskGroups}
            >
              {savingSectionLink ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link Section"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkingItem} onOpenChange={(open) => !open && closeLinkItemModal()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4 text-sm-primary" />
              Link BoQ Item
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeLinkItemModal}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">BoQ Item:</span>{" "}
                <span className="font-medium text-foreground">{linkingItem?.name}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {linkingItem?.sectionTaskGroup != null
                  ? "Showing tasks from the linked task group for this section."
                  : "This section is not linked to a task group yet, so tasks are grouped by task group for selection."}
              </div>
            </div>

            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={taskSearchTerm}
                onChange={(e) => setTaskSearchTerm(e.target.value)}
                placeholder="Search tasks or task groups..."
                className="h-9 pl-9 text-sm"
              />
            </div>

            <div className="max-h-[420px] overflow-y-auto pr-1 space-y-3">
              {loadingTaskOptions ? (
                <div className="flex items-center justify-center py-10 text-sm text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading tasks...
                </div>
              ) : groupedTaskOptions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-3 py-10 text-sm text-muted-foreground text-center">
                  No tasks found.
                </div>
              ) : (
                groupedTaskOptions.map((group) => (
                  <div key={group.key} className="rounded-lg border border-border/60 overflow-hidden">
                    <div className="flex items-center justify-between bg-muted/30 px-3 py-2">
                      <p className="text-sm font-medium text-foreground truncate">
                        {group.groupName}
                      </p>
                      <span className="text-[11px] text-muted-foreground">
                        {group.tasks.length} task{group.tasks.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="divide-y divide-border/40">
                      {group.tasks.map((task) => {
                        const isSelected = String(task.id) === String(selectedTaskId);

                        return (
                          <button
                            key={task.id}
                            type="button"
                            onClick={() => setSelectedTaskId(String(task.id))}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? "bg-sm-primary/5" : "hover:bg-muted/20"
                              }`}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {task.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {task.status || "No status"}
                              </p>
                            </div>
                            <div
                              className={`h-4 w-4 rounded-full border shrink-0 ${isSelected
                                ? "border-sm-primary bg-sm-primary"
                                : "border-border"
                                }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <Button
              className="w-full h-9 text-sm bg-sm-primary text-white"
              onClick={handleLinkItem}
              disabled={!selectedTaskId || savingItemLink || loadingTaskOptions}
            >
              {savingItemLink ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link Task"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showGenerateConfirm} onOpenChange={setShowGenerateConfirm}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Generate Task Structure</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-lg bg-warning/10 border border-warning/30 p-3 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">This will create:</p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                  <li>• {sections.length} Task Groups (from sections)</li>
                  <li>• {totals.totalItems} Tasks (from BoQ items)</li>
                  <li>• Budget will be auto-linked</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-9 text-sm"
                onClick={() => setShowGenerateConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-9 text-sm bg-sm-primary text-white"
                onClick={async () => {
                  await onGenerateTasks();
                  setShowGenerateConfirm(false);
                }}
                disabled={generatingTasks || deletingSectionId != null || deletingItemId != null}
              >
                {generatingTasks ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoQSetup;
